import { aliasColors, brandColors, brandSpace, fontFamily, fontWeight, getPhoneTextStyle } from '../../../../theme';

function modalText({
  fontSize,
  lineHeight,
  fontWeight: weight,
  letterSpacing = 0,
}: {
  fontSize: number;
  lineHeight: number;
  fontWeight: number;
  letterSpacing?: number;
}) {
  return {
    fontFamily: fontFamily.primary,
    fontSize: `${fontSize}px`,
    lineHeight: `${lineHeight}px`,
    letterSpacing: `${letterSpacing}px`,
    fontWeight: weight,
  };
}

/** Breakpoint layouts for the login dialog. */
export const AUTH_MODAL_LAYOUT = {
  PHONE: 'phone',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
} as const;

export type AuthModalLayout = (typeof AUTH_MODAL_LAYOUT)[keyof typeof AUTH_MODAL_LAYOUT];

export const AUTH_MODAL_COPY = {
  title: 'Login or Sign up',
  subtitle: 'Enter your mobile number to continue',
  mobileLabel: 'Mobile Number',
  mobilePlaceholder: 'Enter mobile number',
  countryCode: '+91',
  countryLabel: 'Country code',
  getOtp: 'Get OTP',
  otpSubtitle: 'Enter the 6-digit code we sent to your number',
  otpSubtitleWhatsapp: 'Enter the 6-digit code we sent to your WhatsApp',
  otpLabel: 'Enter OTP',
  change: 'Change',
  resend: 'Resend OTP in 0:24',
  whatsapp: 'Get it on WhatsApp',
  sms: 'Get it by SMS',
  verify: 'Verify & continue',
  profileTitle: 'What should we call you?',
  profileSubtitle:
    'Just a first name is fine. You’ll add traveller names exactly as on ID when you book.',
  nameLabel: 'Your name',
  continue: 'Continue',
  skip: 'Skip for now',
  socialTitle: 'One last step',
  socialSubtitle: 'Add your mobile number for booking updates, e-tickets and account recovery',
  /** Only for an email that already had an account without a mobile; never after a new signup. */
  phoneMissingWarning: 'This email has no mobile number linked yet. Add and verify it to get booking updates and recover your account.',
  signedIn: 'Signed in with Google',
  differentAccount: 'Use a different account',
  socialEmail: 'arya.sharma@gmail.com',
  divider: 'or continue with',
  google: 'Continue with Google',
  apple: 'Continue with Apple',
  legalLead: "By continuing, you agree to Yovotrip's ",
  terms: 'Terms of Service',
  legalAnd: ' and ',
  privacy: 'Privacy Policy',
  close: 'Close',
  previous: 'Previous destination',
  next: 'Next destination',
  logo: 'YovoTrip',
};

/** Colors taken from Phone UI tokens. Social buttons use raw white from the frame. */
export const AUTH_MODAL_COLOR = {
  surface: brandColors.neutrals.baseWhite,
  canvas: aliasColors.background.bg03,
  title: brandColors.primary.yovoRed400,
  text: brandColors.neutrals.base1000,
  muted: brandColors.neutrals.grey600,
  field: aliasColors.background.bg03,
  country: brandColors.neutrals.grey200,
  line: brandColors.neutrals.grey300,
  inactive: brandColors.neutrals.grey500,
  onInactive: brandColors.neutrals.baseWhite,
  social: '#FFFFFF',
  scrim: brandColors.neutrals.base1000,
  shadow: brandColors.neutrals.baseBlack,
  dot: brandColors.neutrals.baseWhite,
  warningSurface: brandColors.semantic.yellow100,
  warningText: brandColors.semantic.yellow400,
};

export const AUTH_MODAL_METRICS = {
  radius: brandSpace.xxxl,
  curve: brandSpace.curve,
  height: 640,
  desktopWidth: 1060,
  tabletWidth: 760,
  desktopCarousel: 440,
  tabletCarousel: 320,
  phoneCarousel: 340,
  desktopForm: 440,
  tabletForm: 360,
  phoneOverlap: brandSpace.xl4,
  control: brandSpace.smallGaps,
  fieldHeight: brandSpace.largeGaps,
  inset: brandSpace.xxxl,
  edge: brandSpace.xxl,
  sectionGap: brandSpace.xxxl,
  headingGap: brandSpace.m,
  stackGap: brandSpace.l,
  dividerGap: brandSpace.xl,
  fieldGap: brandSpace.m,
  inputGap: 10,
  captionGap: 10,
  chipBlur: 6,
  desktopFormTop: 92,
  tabletFormTop: 84,
  phoneFormTop: 32,
  tabletCloseTop: brandSpace.xxl,
  phoneControlBottom: 52,
  dot: brandSpace.s,
  dotActive: 20,
  scrimAlpha: 0.45,
  shadowBlur: 64,
  shadowAlpha: 0.35,
  /** Top offset for a side-by-side card on a tall portrait screen. */
  cardTop: brandSpace.gaps,
  phoneDigits: 10,
  /** Groups of five, as in 98765 43210. */
  phoneGroup: 5,
  /** OTP box height from the Enter OTP frames. The space scale has no 56 step. */
  otpBoxHeight: 56,
  /** Focus ring on the active OTP box. */
  otpFocusBorder: 1.5,
  desktopOtpFormTop: 113,
  tabletOtpFormTop: 105,
  /** Form offset on the profile frames. The space scale has no 109 or 90 step. */
  desktopProfileFormTop: 109,
  tabletProfileFormTop: 90,
  /** Form offset on the One last step frames. The space scale has no 125 or 117 step. */
  desktopSocialFormTop: 125,
  tabletSocialFormTop: 117,
  /** Hairline on the linked-account card. */
  accountBorder: 1,
  /** Gap between the provider line and the email. */
  accountLineGap: brandSpace.xxs,
};

/** Dialog open/close and step-to-step motion. Reduced-motion users get no step slide. */
export const AUTH_MODAL_MOTION = {
  enterMs: 380,
  exitMs: 220,
  stepMs: 360,
  /** How far a new step starts from its resting place. */
  stepOffset: 48,
  /** Backdrop blur behind the card. */
  scrimBlur: 4,
  easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
};

/** Type that the Phone UI scale does not list as its own step, matched to the frame. */
export const AUTH_MODAL_TYPE = {
  title: modalText({
    fontSize: 32,
    lineHeight: 38,
    letterSpacing: -0.32,
    fontWeight: fontWeight.bold,
  }),
  subtitle: modalText({
    fontSize: 15,
    lineHeight: 22,
    fontWeight: fontWeight.regular,
  }),
  label: modalText({
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.medium,
  }),
  code: modalText({
    fontSize: 16,
    lineHeight: 22,
    fontWeight: fontWeight.semiBold,
  }),
  input: modalText({
    fontSize: 16,
    lineHeight: 20,
    fontWeight: fontWeight.regular,
  }),
  phoneValue: getPhoneTextStyle('large/large-500'),
  change: modalText({
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.semiBold,
  }),
  digit: getPhoneTextStyle('headline-small/headline-s-600'),
  helper: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.regular,
  }),
  helperAction: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.semiBold,
  }),
  otp: modalText({
    fontSize: 16,
    lineHeight: 22,
    fontWeight: fontWeight.semiBold,
  }),
  divider: modalText({
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.regular,
  }),
  social: modalText({
    fontSize: 16,
    lineHeight: 24,
    fontWeight: fontWeight.medium,
  }),
  legal: modalText({
    fontSize: 12,
    lineHeight: 16,
    fontWeight: fontWeight.regular,
  }),
  nameValue: modalText({
    fontSize: 16,
    lineHeight: 20,
    fontWeight: fontWeight.medium,
  }),
  chip: modalText({
    fontSize: 13,
    lineHeight: 16,
    fontWeight: fontWeight.medium,
  }),
  slideTitle: modalText({
    fontSize: 28,
    lineHeight: 34,
    letterSpacing: -0.28,
    fontWeight: fontWeight.bold,
  }),
  slideBody: modalText({
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.regular,
  }),
};

/**
 * Phone sheet scale. The desktop frames are 32px titles and 48px fields;
 * a 393px-wide phone cannot fit that sheet without clipping the actions.
 */
export const AUTH_MODAL_TYPE_PHONE = {
  ...AUTH_MODAL_TYPE,
  title: modalText({
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.24,
    fontWeight: fontWeight.bold,
  }),
  subtitle: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.regular,
  }),
  label: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.medium,
  }),
  code: modalText({
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.semiBold,
  }),
  input: modalText({
    fontSize: 15,
    lineHeight: 20,
    fontWeight: fontWeight.regular,
  }),
  change: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.semiBold,
  }),
  digit: modalText({
    fontSize: 20,
    lineHeight: 24,
    fontWeight: fontWeight.semiBold,
  }),
  helper: modalText({
    fontSize: 12,
    lineHeight: 16,
    fontWeight: fontWeight.regular,
  }),
  helperAction: modalText({
    fontSize: 12,
    lineHeight: 16,
    fontWeight: fontWeight.semiBold,
  }),
  otp: modalText({
    fontSize: 15,
    lineHeight: 20,
    fontWeight: fontWeight.semiBold,
  }),
  divider: modalText({
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.regular,
  }),
  social: modalText({
    fontSize: 15,
    lineHeight: 20,
    fontWeight: fontWeight.medium,
  }),
  legal: modalText({
    fontSize: 11,
    lineHeight: 15,
    fontWeight: fontWeight.regular,
  }),
  nameValue: modalText({
    fontSize: 15,
    lineHeight: 20,
    fontWeight: fontWeight.medium,
  }),
};

/** Phone spacing that pairs with the smaller type so the sheet fits a short screen. */
export const AUTH_MODAL_PHONE_METRICS = {
  fieldHeight: 44,
  sectionGap: brandSpace.xl,
  headingGap: brandSpace.s,
  edge: brandSpace.xl,
  phoneOverlap: brandSpace.xl,
  otpBoxHeight: 48,
};

export function authModalType(layout: AuthModalLayout) {
  return layout === AUTH_MODAL_LAYOUT.PHONE ? AUTH_MODAL_TYPE_PHONE : AUTH_MODAL_TYPE;
}

export function authModalMetrics(layout: AuthModalLayout) {
  if (layout !== AUTH_MODAL_LAYOUT.PHONE) return AUTH_MODAL_METRICS;
  return { ...AUTH_MODAL_METRICS, ...AUTH_MODAL_PHONE_METRICS };
}


/** Scrim painted over each destination photo. */
export const AUTH_MODAL_CAROUSEL_SCRIM =
  'linear-gradient(180deg, rgba(17, 15, 26, 0.55) 0%, rgba(17, 15, 26, 0.05) 30%, rgba(17, 15, 26, 0.15) 55%, rgba(17, 15, 26, 0.7) 100%)';
